import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { ISelectOption } from 'shared/components/ui/Select/Select';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { isMobileDevice } from 'shared/utils/isMobileDevice';

import QuestionIcon from 'assets/question.svg?react';

interface IDropdownProps {
  open: boolean;
  list: ISelectOption[];
  onClick: (option: ISelectOption) => void;
  active?: string | number;
  position: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
  shouldShowTooltip?: boolean;
}

const Dropdown = ({
  open,
  list,
  onClick,
  position,
  active,
  shouldShowTooltip = true,
}: IDropdownProps) => {
  const { t } = useTranslation();
  const [showTip, setShowTip] = React.useState<null | number>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const isQuestionIconVisible = useMemo(
    () => shouldShowTooltip && !isMobileDevice() && !isMobile,
    [shouldShowTooltip, isMobile],
  );

  return (
    <AnimatedTransition show={open} asFragment>
      <div
        className="absolute z-50 w-[196px] rounded-s border border-border bg-blue-menu"
        style={{ ...position }}
      >
        <ul className="flex cursor-pointer flex-col items-center text-base">
          {list?.length > 0 &&
            list.map((item, index) => (
              <li
                className={`relative flex w-[10.875rem] justify-between py-2 sm:py-3 ${
                  index !== list.length - 1 ? 'border-b border-border' : ''
                } ${item.value == active ? 'text-yellow-text' : 'text-white'} `}
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(item);
                }}
              >
                {t(item.label)}
                <AnimatedTransition show={index === showTip}>
                  <div className="absolute left-[110%] top-0 w-[15.625rem] lg:w-[25rem]">
                    <div className="tip relative z-50 rounded-lg bg-blue-border px-4 py-[5px] sm:py-3">
                      <p className="text-md text-white sm:text-base">{t(item.tooltip!)}</p>
                    </div>
                  </div>
                </AnimatedTransition>
                {isQuestionIconVisible && item.tooltip && (
                  <QuestionIcon
                    className="cursor-pointer"
                    onMouseEnter={() => setShowTip(index)}
                    onMouseLeave={() => setShowTip(null)}
                  />
                )}
              </li>
            ))}
        </ul>
      </div>
    </AnimatedTransition>
  );
};

export default Dropdown;
